import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, LoadScript, Marker, DirectionsRenderer } from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";
import { useLocation } from "react-router-dom"; // Ensure react-router-dom is installed
import { API_URL } from "./config";

const mapContainerStyle = {
  width: "100%",
  height: "100vh",
};

const defaultCenter = {
  lat: -17.8292, // Default center (Harare)
  lng: 31.0522,
};

const RouteDisplay = () => {
  const [directions, setDirections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);

  const location = useLocation();

  // Get coordinates from URL parameters
  const queryParams = new URLSearchParams(location.search);
  const pointA = {
    lat: parseFloat(queryParams.get("latA")) || -17.8292, // Default to Harare CBD
    lng: parseFloat(queryParams.get("lngA")) || 31.0522,
  };

  const pointB = {
    lat: parseFloat(queryParams.get("latB")) || -17.8456, // Default to Harare Airport
    lng: parseFloat(queryParams.get("lngB")) || 31.1275,
  };

  const calculateAndDisplayRoute = async (directionsService) => {
    if (!directionsService) {
      setError("Google Maps API not loaded");
      setLoading(false);
      return;
    }

    try {
      const result = await directionsService.route({
        origin: pointA,
        destination: pointB,
        travelMode: window.google.maps.TravelMode.DRIVING, // Always set to DRIVING
      });

      setDirections(result);
      setError(null);

      // Fit map to show the entire route
      if (mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(pointA);
        bounds.extend(pointB);
        mapRef.current.fitBounds(bounds);
      }
    } catch (err) {
      setError(`Error calculating route: ${err.message}`);
      setDirections(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = () => {
    const directionsService = new window.google.maps.DirectionsService();
    calculateAndDisplayRoute(directionsService);
  };

  useEffect(() => {
    setLoading(false);
  }, [pointA, pointB]);

  return (
    <div className="relative h-screen">
      <LoadScript
        googleMapsApiKey={getGoogleMapsApiKey()}
        onLoad={handleLoad}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={12}
          onLoad={(map) => (mapRef.current = map)}
        >
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: "#ff0000",
                  strokeWeight: 5,
                  strokeOpacity: 1,
                },
              }}
            />
          )}
          {directions && <DirectionsRenderer directions={directions} />}
        </GoogleMap>
      </LoadScript>

      {/* Loading Indicator */}
      {loading && (
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                        bg-white p-4 rounded-lg shadow-lg z-10"
        >
          Loading ...
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="absolute bottom-4 left-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
          {error}
        </div>
      )}
    </div>
  );
};

export default RouteDisplay;
